import React from 'react';

import { Box, Rating, Skeleton, Stack, Tooltip, Typography } from '@mui/material';

import { useUserConnections } from '../hooks/useUserConnections';
import { UserAuthProfile, UserPerson } from '../types';
import { UserStats } from '../types/networking';
import SocialIconsContainer from 'components/SocialIconsContainer';
import Spin from 'components/Spin';
import { useTheme } from 'hooks/useTheme';

type Props = {
  userDetails: UserPerson | null;
  userProfile: UserAuthProfile | null;
  loadingDetails: boolean;
  loadingProfile: boolean;
  userStats: UserStats | null;
  loadingStats: boolean;
  onRatingChange: (newValue: number) => void;
  loggedUserId: string;
  selectedUserId: string;
};

const UserCard: React.FC<Props> = ({
  userDetails,
  userProfile,
  loadingDetails,
  loadingProfile,
  userStats,
  loadingStats,
  onRatingChange,
  loggedUserId,
  selectedUserId,
}) => {
  const { theme } = useTheme();
  const { connections, loading: loadingConnections } = useUserConnections(
    selectedUserId,
    loggedUserId
  );

  if (loadingDetails || loadingProfile) {
    return <Skeleton variant="rectangular" height={120} />;
  }

  if (!userDetails || !userProfile) return null;

  return (
    <Stack direction="column" spacing={0} justifyContent="center" alignItems="start" height="100%">
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="h3" fontWeight={600} color="primary">
          {userDetails.first_name} {userDetails.last_name}
        </Typography>

        {!loadingConnections && (
          <Typography variant="h6" color="text.primary" fontWeight={700}>
            {connections?.level && connections?.level > 0 ? <>- {connections?.level}º</> : ''}
          </Typography>
        )}
      </Stack>
      <Typography variant="h6">{userProfile?.company?.name || ''}</Typography>
      <Typography variant="h6" color="primary">
        {userProfile?.company_role || ''}
      </Typography>

      <Box my={0.8}>
        <Stack
          direction="column"
          spacing={1}
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <Box mt={2}>
            {userProfile.social_networks.length > 0 ? (
              <SocialIconsContainer
                socialNetworks={userProfile.social_networks}
                socialLoading={false}
                socialError={null}
                circle
                iconSize={16}
              />
            ) : (
              ''
            )}
          </Box>
        </Stack>
      </Box>

      {loadingStats && <Spin />}
      {!loadingStats && userStats && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            pb: 1,
            mt: 1,
          }}
        >
          {(() => {
            const isOwnProfile = loggedUserId === selectedUserId;
            const hasConnection = connections?.level && connections?.level > 0;
            const isConnected = connections?.level_code === 'CONNECTION';

            const canRate = !isOwnProfile && isConnected;

            const ratingTooltip = isOwnProfile
              ? 'Você não pode avaliar seu próprio perfil'
              : !isConnected
                ? 'Necessário ser uma conexão para avaliar o usuário'
                : 'Clique para avaliar o usuário';

            return (
              <Tooltip title={ratingTooltip} arrow placement="top">
                <span>
                  <Rating
                    size="large"
                    value={userStats?.nps}
                    onChange={(event, newValue) => {
                      if (canRate) {
                        onRatingChange(newValue || 0);
                      }
                    }}
                    disabled={!canRate}
                    sx={{
                      '& .MuiRating-icon': {
                        color: canRate ? theme.primary : theme.primary,
                      },
                      '& .MuiRating-iconFilled': {
                        color: canRate ? theme.primary : theme.primary,
                      },
                      '& .MuiRating-iconHover': {
                        color: canRate ? theme.primary : theme.primary,
                      },
                    }}
                  />
                </span>
              </Tooltip>
            );
          })()}
          <Box display="flex" gap={0.5}>
            <Typography fontWeight="bold" color="text.primary" fontSize={16}>
              ({userStats.nps})
            </Typography>
            {/* <Typography fontWeight="bold" color="text.primary" fontSize={20}>
              ({userStats.nps * 10})
            </Typography> */}
          </Box>
        </Box>
      )}
    </Stack>
  );
};

export default React.memo(UserCard);
